import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Brain } from "lucide-react";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import PasswordInput from "../components/PasswordInput";
import { toast } from "sonner";

// iter130 Phase A — /auth/reset?t=<token>
// Landing page a user hits when they click a Resend password-reset
// link. Prompts for a new password, then calls the backend to consume
// the token and bump `token_version` (server-side session invalidation).
export default function ResetPassword() {
  const { confirmPasswordReset } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [token] = useState(() => params.get("t") || "");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) setError("This reset link is missing its token. Please request a new one.");
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    const res = await confirmPasswordReset({ token, newPassword });
    setSubmitting(false);
    if (!res.ok) {
      setError(res.error || "Reset failed. The link may have expired.");
      return;
    }
    toast.success("Password updated. Signed out of all devices — please sign in.");
    nav("/login");
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[var(--bt-bg)]">
      <form
        onSubmit={onSubmit}
        data-testid="reset-form"
        className="w-full max-w-md space-y-5 bg-[var(--bt-surface)] border border-[var(--bt-border)] rounded-sm p-8"
      >
        <div className="flex items-center gap-3">
          <span className="w-8 h-8 rounded-sm bg-[var(--bt-accent)] flex items-center justify-center">
            <Brain className="w-4 h-4 text-black" strokeWidth={2.5} />
          </span>
          <span className="font-heading text-lg">Brain<span className="text-[var(--bt-accent)]">TRADES</span></span>
        </div>

        <div>
          <p className="bt-label">Set new password</p>
          <h1 className="font-heading text-2xl mt-1">Choose a new password</h1>
          <p className="text-sm text-[var(--bt-text-muted)] mt-1">
            Setting a new password will sign you out of all other devices.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="new-pw" className="bt-label">New password (min 8 chars)</Label>
          <PasswordInput
            id="new-pw"
            required autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            testId="reset-new-password-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirm-pw" className="bt-label">Confirm password</Label>
          <PasswordInput
            id="confirm-pw"
            required autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            testId="reset-confirm-password-input"
          />
        </div>

        {error && (
          <div data-testid="reset-error" className="text-sm text-[var(--bt-danger)] border border-[var(--bt-danger)]/40 bg-[var(--bt-danger)]/10 px-3 py-2 rounded-sm">
            {error}
          </div>
        )}

        <Button
          type="submit"
          disabled={submitting || !token}
          data-testid="reset-submit-btn"
          className="w-full h-11 rounded-sm bg-[var(--bt-accent)] hover:bg-[var(--bt-accent-hover)] text-black font-bold"
        >
          {submitting ? "Updating…" : "Reset password"}
        </Button>

        <p className="text-sm text-center text-[var(--bt-text-muted)]">
          <Link to="/login" className="text-[var(--bt-accent)] hover:underline">
            Back to sign in
          </Link>
        </p>
      </form>
    </div>
  );
}
